import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-describedby", "aria-invalid", "aria-labelledby", "aria-required", "calendarPage", "clearLabel", "DateInput", "disabled", "endInputRef", "error", "format", "id", "inputRef", "min", "minValidationMessage", "max", "maxValidationMessage", "onCalendarPageChange", "onChange", "onOpenChange", "open", "presets", "readOnly", "required", "showPresets", "use", "today", "tz", "value", "defaultValue"];
import unescapedText from 'I18n/utils/unescapedText';
import { useContext, useMemo, useRef, useState, useEffect, memo } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import styled from 'foundations-theming/utils';
import { FieldsetContext } from '../../context/FieldsetContext';
import { compare } from '../../utils/SimpleDate';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { Controllable } from '../Controllable';
import ShareInput from '../ShareInput/ShareInput';
import { LAST_MONTH, LAST_QUARTER, LAST_THIRTY_DAYS, LAST_THREE_MONTHS, LAST_WEEK, LAST_YEAR, THIS_MONTH, THIS_QUARTER, THIS_WEEK, THIS_YEAR, TODAY, YESTERDAY } from '../Dates/dateRangePresets';
import { getPageFromDate } from '../../utils/calendarUtils';
import { DateRangePopover } from '../Dates/internal/DateRangePopover';
import { UsesToday } from '../Dates/internal/UsesToday';
import { DateInput as DateInputComponent } from '../DateInput';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useMicroDateRangeContext } from '../MicroDateRange/MicroDateRangeProvider';
import { callIfPossible } from '../../utils/functions';
import { deepMerge } from '../../utils/deepMerge';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "DateRange__Wrapper",
  componentId: "wlya94-0"
})(({
  $cssStyles,
  $styleOverrides
}) => {
  return deepMerge(Object.assign({
    display: 'flex',
    '& > *': {
      flex: '1 1 0%'
    }
  }, $cssStyles), $styleOverrides);
});
const Separator = styled.div.withConfig({
  displayName: "DateRange__Separator",
  componentId: "wlya94-1"
})(({
  theme
}) => {
  return {
    flexGrow: '0',
    alignSelf: 'center',
    flexBasis: 'auto',
    paddingBlock: theme.space['0'],
    paddingInline: theme.space['250']
  };
});
const DateRangeComponent = props => {
  const {
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      'aria-labelledby': ariaLabelledBy,
      'aria-required': ariaRequired,
      calendarPage,
      clearLabel = I18n.text('ui.datePicker.clear'),
      DateInput = DateInputComponent,
      disabled,
      endInputRef: endInputRefProp,
      error,
      format = 'L',
      id,
      inputRef,
      min,
      minValidationMessage = I18n.text('ui.datePicker.dateBelowMin'),
      max,
      maxValidationMessage = I18n.text('ui.datePicker.dateAboveMax'),
      onCalendarPageChange,
      onChange,
      onOpenChange,
      open,
      presets = [TODAY, YESTERDAY, THIS_WEEK, LAST_WEEK, THIS_MONTH, LAST_MONTH, LAST_THIRTY_DAYS, LAST_THREE_MONTHS, THIS_QUARTER, LAST_QUARTER, LAST_YEAR, THIS_YEAR],
      readOnly,
      required,
      showPresets = true,
      use,
      today,
      tz,
      value,
      defaultValue
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const computedValue = useMemo(() => {
    const obj = {
      presetId: ''
    }; // Default presetId and leave startDate and endDate undefined

    const sourceValue = value || defaultValue;
    if (sourceValue) {
      const {
        presetId,
        startDate,
        endDate,
        getValue
      } = sourceValue;
      if (presetId) obj.presetId = presetId;
      if (startDate !== undefined) obj.startDate = startDate;
      if (endDate !== undefined) obj.endDate = endDate;
      if (getValue) Object.assign(obj, getValue(tz));
    }
    return obj;
  }, [value, defaultValue, tz]);
  const {
    startDate,
    endDate
  } = computedValue;

  // Values used for internal state
  const [startDateInputValue, setStartDateInputValue] = useState(startDate);
  const [endDateInputValue, setEndDateInputValue] = useState(endDate);
  const computedInternalValue = useMemo(() => Object.assign({}, computedValue, {
    startDate: startDateInputValue,
    endDate: endDateInputValue
  }), [computedValue, startDateInputValue, endDateInputValue]);

  // Values used in event handlers
  const startDateInputValueRef = useRef(undefined);
  startDateInputValueRef.current = startDateInputValue;
  const endDateInputValueRef = useRef(undefined);
  endDateInputValueRef.current = endDateInputValue;

  // Handle external value changes
  useEffect(() => {
    if (startDateInputValue !== startDate) {
      setStartDateInputValue(startDate);
    }
    if (endDateInputValue !== endDate) {
      setEndDateInputValue(endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);
  const fieldsetContext = useContext(FieldsetContext);
  const computedDisabled = disabled || fieldsetContext.disabled;
  const microDateRange = useMicroDateRangeContext();
  const inputProps = {
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
    disabled: computedDisabled,
    format,
    max,
    min,
    readOnly,
    required,
    use
  };
  const [activeBound, setActiveBound] = useState('start');
  const prevActiveBound = usePrevious(activeBound);
  const calendarId = useUniqueId(`daterange-`);
  const startLabelId = useUniqueId(`daterange-start-label-`);
  const endLabelId = useUniqueId(`daterange-end-label-`);
  // Ticket filed here to fix this issue: https://git.hubteam.com/HubSpot/UIComponents/issues/11740
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const endInputRef = endInputRefProp || useRef(null);
  const handleKeyDown = evt => {
    const {
      key
    } = evt;
    if (key === 'Escape') {
      if (!open || !onOpenChange) return;
      evt.preventDefault();
      evt.stopPropagation();
      onOpenChange(syntheticEvent(false));
    }
  };
  return /*#__PURE__*/_jsx(DateRangePopover
  // Edge case: If `open` and `activeBound` becomes null (e.g. when window loses focus), reset the activeBound
  , {
    activeBound: open && activeBound === null ? prevActiveBound : activeBound,
    calendarId: calendarId,
    calendarPage: calendarPage,
    clearLabel: clearLabel,
    max: max,
    maxValidationMessage: maxValidationMessage,
    min: min,
    minValidationMessage: minValidationMessage,
    onActiveBoundChange: evt => {
      (evt.target.value === 'end' ? endInputRef : inputRef).current.focus();
    },
    onCalendarPageChange: onCalendarPageChange,
    onChange: evt => {
      callIfPossible(onChange, evt);

      // If the user just picked an end date, close the popover.
      const newValue = evt.target.value;
      if (newValue.endDate !== endDate) callIfPossible(onOpenChange, syntheticEvent(false));
    },
    onOpenChange: onOpenChange,
    open: open && !computedDisabled && !readOnly,
    presets: presets,
    showPresets: showPresets,
    today: today,
    tz: tz,
    value: computedInternalValue,
    onFocusLeave: () =>
    // If focus leaves the popover and either input isn't active, close it
    activeBound === null && callIfPossible(onOpenChange, syntheticEvent(false)),
    children: /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, domProps, {
      $cssStyles: cssStyles,
      $styleOverrides: (microDateRange === null || microDateRange === void 0 ? void 0 : microDateRange.wrapperStyleOverrides) || {},
      children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
        id: startLabelId,
        children: unescapedText('ui.dateRange.startDate')
      }), /*#__PURE__*/_jsx(ScreenReaderOnly, {
        id: endLabelId,
        children: unescapedText('ui.dateRange.endDate')
      }), /*#__PURE__*/_jsx(DateInput, Object.assign({}, inputProps, {
        "aria-labelledby": [ariaLabelledBy, startLabelId].filter(Boolean).join(' '),
        error: error || !!(startDateInputValue && endDate && compare(startDateInputValue, endDate) === 1),
        id: id /* support the htmlFor from UIFormControl */,
        inputRef: inputRef,
        onChange: ({
          target: {
            value: newStartDate
          }
        }) => {
          setStartDateInputValue(newStartDate);
          if (!endDateInputValue || newStartDate === null || compare(newStartDate, endDateInputValue) !== 1) {
            // Make sure the calendar view shows the new selection.
            callIfPossible(onCalendarPageChange, syntheticEvent(getPageFromDate(newStartDate)));
            callIfPossible(onChange, syntheticEvent(Object.assign({}, computedValue, {
              startDate: newStartDate,
              endDate: endDateInputValueRef.current
            })));
          }
        },
        onFocus: () => {
          if (readOnly || computedDisabled) return;
          setActiveBound('start');
          callIfPossible(onCalendarPageChange, syntheticEvent(getPageFromDate(startDateInputValue)));
        },
        onBlur: () => {
          if (readOnly || computedDisabled) return;
          setActiveBound(null);
        },
        onOpenChange: onOpenChange,
        onKeyDown: handleKeyDown,
        open: false,
        originDate: endDate,
        tz: tz,
        value: startDateInputValue
      })), /*#__PURE__*/_jsx(Separator, {
        children: unescapedText('ui.dateRange.to')
      }), /*#__PURE__*/_jsx(DateInput, Object.assign({}, inputProps, {
        "aria-labelledby": [ariaLabelledBy, endLabelId].filter(Boolean).join(' '),
        error: error || !!(startDate && endDateInputValue && compare(startDate, endDateInputValue) === 1),
        inputRef: endInputRef,
        onChange: ({
          target: {
            value: newEndDate
          }
        }) => {
          setEndDateInputValue(newEndDate);
          if (!startDateInputValue || newEndDate === null || compare(startDateInputValue, newEndDate) !== 1) {
            // Make sure the calendar view shows the new selection.
            callIfPossible(onCalendarPageChange, syntheticEvent(getPageFromDate(newEndDate)));
            callIfPossible(onChange, syntheticEvent(Object.assign({}, computedValue, {
              startDate: startDateInputValueRef.current,
              endDate: newEndDate
            })));
          }
        },
        onFocus: () => {
          if (readOnly || computedDisabled) return;
          setActiveBound('end');
          callIfPossible(onCalendarPageChange, syntheticEvent(getPageFromDate(endDateInputValue)));
        },
        onBlur: () => {
          if (readOnly || computedDisabled) return;
          setActiveBound(null);
        },
        onOpenChange: onOpenChange,
        onKeyDown: handleKeyDown,
        open: false,
        originDate: startDate,
        tz: tz,
        value: endDateInputValue
      }))]
    }))
  });
};
export const DateRangeStorybook = DateRangeComponent;
export const DateRange = ShareInput(Controllable(UsesToday( /*#__PURE__*/memo(DateRangeComponent)), ['calendarPage', 'open', 'value']));